/**
 * Tiny TTL-bounded localStorage cache for PUBLIC, user-independent reference
 * data (exchange rates, etc.).
 *
 * Why this exists: endpoints like `/rest/v1/exchange_rates` serve the same
 * value to every visitor and only change every few minutes. Reading them on
 * every component mount / tab refocus / page reload hammers the API for no
 * benefit. A short TTL cache collapses those into one read per window, and —
 * because it is backed by localStorage — the cached value survives full page
 * reloads and is shared across tabs on the device.
 *
 * SAFETY: only ever cache responses that do NOT depend on the authenticated
 * user. Never put per-user data (balance, bets, KYC) through this helper — the
 * entry is visible to any code/tab on the same origin.
 *
 * SSR-safe: every function is a no-op / returns null when `window` or
 * `localStorage` is unavailable.
 */

interface TtlEntry<T> {
  /** Cached value. */
  v: T
  /** Epoch ms when the entry was written. */
  t: number
}

export interface TtlCacheHit<T> {
  value: T
  /** Epoch ms when the value was originally cached. */
  cachedAt: number
}

function getStore(): Storage | null {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return null
    return window.localStorage
  } catch {
    // Accessing localStorage can throw (privacy mode, blocked cookies).
    return null
  }
}

/**
 * Read a cached entry along with its write timestamp, but only if it is still
 * within `ttlMs`. Returns null when missing, expired, or unparseable.
 */
export function readTtlCacheEntry<T>(key: string, ttlMs: number): TtlCacheHit<T> | null {
  const store = getStore()
  if (!store) return null
  try {
    const raw = store.getItem(key)
    if (!raw) return null
    const entry = JSON.parse(raw) as TtlEntry<T>
    if (!entry || typeof entry.t !== 'number') return null
    if (Date.now() - entry.t > ttlMs) return null
    return { value: entry.v, cachedAt: entry.t }
  } catch {
    return null
  }
}

/** Convenience wrapper around {@link readTtlCacheEntry} that returns only the value. */
export function readTtlCache<T>(key: string, ttlMs: number): T | null {
  return readTtlCacheEntry<T>(key, ttlMs)?.value ?? null
}

/** Persist a value with the current timestamp. Best-effort — quota/serialization errors are swallowed. */
export function writeTtlCache<T>(key: string, value: T): void {
  const store = getStore()
  if (!store) return
  try {
    const entry: TtlEntry<T> = { v: value, t: Date.now() }
    store.setItem(key, JSON.stringify(entry))
  } catch {
    // ignore — the cache is an optimization, not a source of truth.
  }
}

/** Remove every cache entry whose key starts with `prefix`. */
export function clearTtlCacheByPrefix(prefix: string): void {
  const store = getStore()
  if (!store) return
  try {
    const toRemove: string[] = []
    for (let i = 0; i < store.length; i++) {
      const k = store.key(i)
      if (k && k.startsWith(prefix)) toRemove.push(k)
    }
    toRemove.forEach((k) => store.removeItem(k))
  } catch {
    // ignore
  }
}
